import { useParams } from 'react-router-dom';
import { FirebaseProvider } from '../../contexts/FirebaseContext';
import { useCorrelations } from '../../hooks/useCorrelations';
import { useTranslation } from '../../hooks/useTranslation';
import CorrelationCard from './CorrelationCard';
import './CorrelationsPage.css';

function CorrelationsContent() {
  const { num } = useParams<{ num: string }>();
  // Convert from 1-indexed URL param to 0-indexed array index
  const questionNumber = parseInt(num || '1', 10);
  const baseQuestionIndex = questionNumber - 1;
  const { translations, t } = useTranslation();

  // Get questions from translations
  const questionsEn = Object.keys(translations.questions.en);
  const answersEn = Object.values(translations.questions.en) as string[][];
  const totalQuestions = questionsEn.length;

  const { stats, loading } = useCorrelations(baseQuestionIndex, totalQuestions);

  // Validate question number (1-indexed)
  if (questionNumber < 1 || questionNumber > totalQuestions) {
    return (
      <div className="correlations-error">
        <p>
          Invalid question number. Please choose a question between 1 and{' '}
          {totalQuestions}.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="correlations-loading">
        <p>Loading correlation data...</p>
      </div>
    );
  }

  if (!stats || !stats.counts[baseQuestionIndex]) {
    return (
      <div className="correlations-error">
        <p>No correlation data available for question {questionNumber}.</p>
      </div>
    );
  }

  const baseQuestionText = questionsEn[baseQuestionIndex];
  const baseAnswers = answersEn[baseQuestionIndex];

  // Get explanation text from translations
  const explanationText =
    t('correlationExplanation', 'en') ||
    `Graph compares relative distribution of opinions to the above question (${
      baseQuestionIndex + 1
    }) within each group of responses to the bottom question`;

  return (
    <div className="correlations-page">
      {/* Render correlation cards for all other questions */}
      {Object.keys(stats.counts[baseQuestionIndex]).map((q2String) => {
        const q2 = parseInt(q2String, 10);
        const correlatedQuestionText = questionsEn[q2];
        const correlatedAnswers = answersEn[q2];
        const counts = stats.counts[baseQuestionIndex][q2];

        return (
          <CorrelationCard
            key={q2}
            baseQuestionIndex={baseQuestionIndex}
            baseQuestionText={baseQuestionText}
            baseAnswers={baseAnswers}
            correlatedQuestionIndex={q2}
            correlatedQuestionText={correlatedQuestionText}
            correlatedAnswers={correlatedAnswers}
            counts={counts}
            totalRespondents={stats.totalResponses}
            explanationText={explanationText
              .replace('{q1}', String(baseQuestionIndex + 1))
              .replace('{q2}', String(q2 + 1))}
          />
        );
      })}
    </div>
  );
}

function CorrelationsPage() {
  return (
    <FirebaseProvider>
      <CorrelationsContent />
    </FirebaseProvider>
  );
}

export default CorrelationsPage;
